import mixpanel from "mixpanel-browser";

/**
 * Thin guard layer over mixpanel-browser. Only this file, init.ts and
 * testing.ts may import the SDK (enforced by catalog.contract.test.ts).
 *
 * Every call is inert until initAnalytics() flips the `ready` latch — files
 * that also compile into canvas-view (which never initialises the app
 * tracker) can import the façade safely.
 */

let ready = false;

export function markAnalyticsReady(): void {
  ready = true;
}

export function isAnalyticsReady(): boolean {
  return ready;
}

/** vitest only — see testing.ts. */
export function resetAnalyticsReadyForTest(): void {
  ready = false;
}

function guard(fn: () => void): void {
  if (!ready) return;
  try {
    fn();
  } catch {
    // Analytics must never break the app.
  }
}

export function track(
  event: string,
  properties?: Record<string, unknown>,
): void {
  guard(() => mixpanel.track(event, properties));
}

export function trackPageView(properties?: Record<string, unknown>): void {
  guard(() => mixpanel.track_pageview(properties));
}

export function registerSuper(
  properties: Record<string, unknown>,
  options?: { persistent?: boolean },
): void {
  guard(() =>
    options
      ? mixpanel.register(properties, options)
      : mixpanel.register(properties),
  );
}

/**
 * Registers keys with defined values and unregisters the rest —
 * mixpanel.register skips undefined values, so a super property whose value
 * went away (a lost plan, a left canvas) would otherwise go stale.
 */
export function syncSuperProps(
  properties: Record<string, unknown>,
  options?: { persistent?: boolean },
): void {
  const defined: Record<string, unknown> = {};
  const gone: string[] = [];
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined) {
      gone.push(key);
    } else {
      defined[key] = value;
    }
  }
  registerSuper(defined, options);
  if (gone.length > 0) {
    unregisterSuper(gone);
  }
}

export function unregisterSuper(keys: string[]): void {
  guard(() => {
    for (const key of keys) {
      mixpanel.unregister(key);
    }
  });
}

export function identify(distinctId: string): void {
  guard(() => mixpanel.identify(distinctId));
}

export function peopleSet(properties: Record<string, unknown>): void {
  guard(() => mixpanel.people.set(properties));
}

export function peopleSetOnce(properties: Record<string, unknown>): void {
  guard(() => mixpanel.people.set_once(properties));
}

export function setGroup(
  groupKey: string,
  groupId: string,
  properties?: Record<string, unknown>,
): void {
  guard(() => {
    mixpanel.set_group(groupKey, groupId);
    if (properties) {
      mixpanel.get_group(groupKey, groupId).set(properties);
    }
  });
}

export function resetAnalytics(): void {
  guard(() => mixpanel.reset());
}
